import { useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { CountryCode } from 'libphonenumber-js';

import { useAuthContext } from 'src/contexts/AuthProvider';
import { GET_COUNTRIES } from 'src/graphql/queries/countries.query';

export type Country = {
    country: CountryCode;
    name: string;
    flag: string;
    callingCode: string;
};

export const useCountries = (filterByAlpha2Codes?: string[]) => {
    const [countryList, setCountryList] = useState([]);
    const { isAuthenticated } = useAuthContext();

    const [fetchCountries, { loading: loadingCountries }] = useLazyQuery(GET_COUNTRIES, {
        fetchPolicy: 'cache-first',
        onCompleted: (data) => {
            localStorage.setItem('countries', JSON.stringify(data?.countries));

            setCountryList(data?.countries);
        },
    });

    const filterCountries = (countriesToFilter) => {
        if (filterByAlpha2Codes && filterByAlpha2Codes.length > 0) {
            return countriesToFilter.filter((country) => {
                return filterByAlpha2Codes.includes(country.alpha2Code);
            });
        }
        return countriesToFilter;
    };

    const countries: Country[] = useMemo(() => {
        if (countryList && !loadingCountries) {
            if (filterByAlpha2Codes && filterByAlpha2Codes.length > 0) {
                const filteredCountries = filterCountries(countryList);
                return filteredCountries.map((country) => {
                    return {
                        country: country.alpha2Code,
                        name: country.name,
                        flag: country.flags.svg,
                        callingCode: country.callingCodes[0].code,
                    };
                });
            }
            return countryList.map((country) => {
                return {
                    country: country.alpha2Code,
                    name: country.name,
                    flag: country.flags.svg,
                    callingCode: country.callingCodes[0].code,
                };
            });
        }

        return [];
    }, [countryList, loadingCountries]);

    useEffect(() => {
        const storedCountries = localStorage.getItem('countries');

        const fetchData = async () => {
            try {
                fetchCountries();
                return;
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        };

        if (isAuthenticated && (!storedCountries || loadingCountries)) {
            fetchData();
        } else if (storedCountries) {
            if (filterByAlpha2Codes && filterByAlpha2Codes.length > 0) {
                const filteredCountries = JSON.parse(storedCountries).filter((country) => {
                    return filterByAlpha2Codes.includes(country.alpha2Code);
                });
                setCountryList(filteredCountries);
                return;
            }
            setCountryList(JSON.parse(storedCountries));
        }
    }, [isAuthenticated, loadingCountries, fetchCountries]);

    return { countries, loadingCountries };
};
